/**
 * One screen, as the preview reads it. Read-only and defensive: `content` and `activities` are
 * required by the schema but a raw working document can be missing either, and refinement 7's case
 * — a screen with NEITHER — is perfectly ordinary while authoring. It renders as a stated empty
 * screen, not as a crash and not as an error.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { Activity, Screen } from './interfaces'
import type { ContentBlock, ScreenLayout } from './types'

interface PreviewActivityItem {
  key: string
  /** 1-based, as an author counts activities. */
  position: number
  activity: Activity
}

interface UsePreviewScreenReturn {
  refContent: ComputedRef<ContentBlock[]>
  refHasContent: ComputedRef<boolean>
  refActivities: ComputedRef<PreviewActivityItem[]>
  refHasActivities: ComputedRef<boolean>
  /** Neither content nor activities — refinement 7's case, rendered rather than judged. */
  refIsEmpty: ComputedRef<boolean>
  refLayout: ComputedRef<ScreenLayout>
  /** The authored forward-button label, `''` when the player's own wording applies. */
  refCta: ComputedRef<string>
  refIsSubmitScreen: ComputedRef<boolean>
  /** The screen's author-only line — layout, forward-button wording, submit flag — composed here
   *  rather than in the template, where the fragments would fight the whitespace rules. */
  refAuthorNote: ComputedRef<string>
}

const usableArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])

export const usePreviewScreen = (screen: () => Screen | null): UsePreviewScreenReturn => {
  const refContent = computed<ContentBlock[]>(() =>
    usableArray<ContentBlock>(screen()?.content),
  )

  const refHasContent = computed<boolean>(() => refContent.value.length > 0)

  const refActivities = computed<PreviewActivityItem[]>(() =>
    usableArray<Activity>(screen()?.activities).map((activity, index) => ({
      // The index, not `activity.id`: ids can be blank or duplicated while a document is being
      // authored, and duplicate `v-for` keys are a rendering bug.
      key: `activity-${index}`,
      position: index + 1,
      activity,
    })),
  )

  const refHasActivities = computed<boolean>(() => refActivities.value.length > 0)

  const refIsEmpty = computed<boolean>(() => !refHasContent.value && !refHasActivities.value)

  const refLayout = computed<ScreenLayout>(() =>
    screen()?.layout === 'two-column' ? 'two-column' : 'single',
  )

  const refCta = computed<string>(() => {
    const cta = screen()?.cta
    return typeof cta === 'string' ? cta.trim() : ''
  })

  const refIsSubmitScreen = computed<boolean>(() => screen()?.isLast === true)

  const refAuthorNote = computed<string>(() => [
    refLayout.value === 'two-column'
      ? 'two-column layout — shown stacked here'
      : 'single-column layout',
    refCta.value ? '' : 'the forward button uses the player\'s own wording',
    refIsSubmitScreen.value ? 'this screen submits the attempt' : '',
  ].filter(Boolean).join(' · '))

  return {
    refContent,
    refHasContent,
    refActivities,
    refHasActivities,
    refIsEmpty,
    refLayout,
    refCta,
    refIsSubmitScreen,
    refAuthorNote,
  }
}
